import { useRef } from "react";
import SearchSelect from "../../../components/admin/SearchSelect";
import { hoyISO } from "../../../utils/formatoFecha";
import { acceptDe, validarArchivo } from "../../../utils/limitesArchivos";
import "./UnidadesProducto.css";

/**
 * Las piezas fisicas de un producto: mismo numero de parte, distinto numero
 * de serie.
 *
 * Una pieza por linea, con las etiquetas una sola vez arriba. Antes cada una
 * era una tarjeta desplegable con dos filas de campos: para cargar cinco habia
 * que abrir, llenar, cerrar y repetir, y la pantalla crecia a lo largo sin
 * dejar ver el conjunto. En una linea se cargan de corrido con el tabulador y
 * se leen las cinco de un vistazo.
 *
 * El "+" copia la ultima dejando el serial vacio: cinco bombas del mismo lote
 * comparten condicion, certificado y fecha, y lo unico que cambia es el serial.
 */

const IconoMas = (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4"
       strokeLinecap="round" aria-hidden="true">
    <path d="M12 5v14M5 12h14" />
  </svg>
);

const IconoClip = (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"
       strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M21 11.5l-8.8 8.8a5 5 0 01-7.1-7.1l9-9a3.3 3.3 0 014.7 4.7l-9 9a1.7 1.7 0 01-2.4-2.4l8.3-8.3" />
  </svg>
);

const IconoEquis = (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2"
       strokeLinecap="round" aria-hidden="true">
    <path d="M6 6l12 12M18 6L6 18" />
  </svg>
);

const IconoBasura = (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
       strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M4 7h16M10 11v6M14 11v6" />
    <path d="M6 7l1 12a2 2 0 002 2h6a2 2 0 002-2l1-12" />
    <path d="M9 7V5a1 1 0 011-1h4a1 1 0 011 1v2" />
  </svg>
);

/* Contador solo para el atributo key de React. No se manda al backend: la
   identidad real de una pieza guardada es producto_unidad_id, y una recien
   agregada todavia no tiene. Sin esto, usar el indice como key hace que al
   borrar una linea del medio las de abajo hereden el texto de la anterior. */
let contadorLocal = 0;
export const nuevaUnidad = (base = {}) => ({
  _key: `nueva-${++contadorLocal}`,
  producto_unidad_id: null,
  numero_serie: "",
  condicion_producto_id: base.condicion_producto_id ?? "",
  certificado_producto_id: base.certificado_producto_id ?? "",
  fecha_certificado: base.fecha_certificado ?? "",
  /* El PDF no se copia de la anterior: el certificado es de cada pieza y
     dos piezas no comparten el mismo escaneo. */
  documento: null,
  url_documento: null,
  estado: "disponible",
});

/** Las que vienen del backend ya tienen id: ese alcanza como key. */
export const desdeBackend = (fila) => ({
  _key: `guardada-${fila.producto_unidad_id}`,
  producto_unidad_id: fila.producto_unidad_id,
  numero_serie: fila.numero_serie ?? "",
  condicion_producto_id: fila.condicion_producto_id ?? "",
  certificado_producto_id: fila.certificado_producto_id ?? "",
  fecha_certificado: fila.fecha_certificado ?? "",
  documento: null,
  url_documento: fila.url_documento ?? null,
  estado: fila.estado ?? "disponible",
});

/** Lo que viaja al backend: sin las marcas internas de la pantalla. */
export const paraEnviar = (unidades) =>
  unidades.map((u) => ({
    producto_unidad_id: u.producto_unidad_id ?? null,
    numero_serie: String(u.numero_serie ?? "").trim(),
    condicion_producto_id: u.condicion_producto_id || null,
    certificado_producto_id: u.certificado_producto_id || null,
    fecha_certificado: u.fecha_certificado || null,
    /* Solo se manda cuando lo quitaron. Si no viene la clave, el backend deja
       el PDF que ya tenia la pieza. */
    ...(u.url_documento === null && !u.documento ? { url_documento: null } : {}),
  }));

/** Los PDF nuevos, por posicion: asi el backend sabe a que pieza va cada uno. */
export const documentosDeUnidades = (unidades) =>
  unidades
    .map((u, i) => (u.documento ? { indice: i, file: u.documento } : null))
    .filter(Boolean);

function UnidadesProducto({
  unidades,
  onChange,
  condiciones,
  certificados,
  opcionesCatalogo,
  disabled,
}) {
  // Para llevar el cursor al serial de la linea recien agregada.
  const ultimaAgregada = useRef(null);

  /* Los seriales repetidos se marcan en la pantalla ademas de en el backend:
     al cargar cinco de corrido es facil pegar el mismo dos veces, y enterarse
     recien al guardar obliga a buscar cual era. */
  const repetidos = new Set();
  const vistos = new Set();
  unidades.forEach((u) => {
    const clave = String(u.numero_serie ?? "").trim().toUpperCase();
    if (!clave) return;
    if (vistos.has(clave)) repetidos.add(clave);
    vistos.add(clave);
  });

  const agregar = () => {
    // Copia la ultima: es la que tiene los datos del lote que se esta cargando.
    const anterior = unidades[unidades.length - 1];
    const nueva = nuevaUnidad(anterior || {});
    ultimaAgregada.current = nueva._key;
    onChange([...unidades, nueva]);
  };

  const quitar = (key) => onChange(unidades.filter((u) => u._key !== key));

  /** El nombre que se muestra: el del archivo nuevo, o el ya guardado. */
  const nombreDoc = (u) =>
    u.documento?.name || String(u.url_documento || "").split("/").pop();

  const tomarDoc = async (key, e) => {
    const input = e.target;
    const file = input.files?.[0];
    input.value = "";
    if (!file) return;

    const rechazo = await validarArchivo(file, "documento");
    if (rechazo) {
      // Un error por linea no cabe: va al mismo renglon que el del serial.
      onChange(unidades.map((u) => (u._key === key ? { ...u, errorDoc: rechazo } : u)));
      return;
    }

    onChange(
      unidades.map((u) =>
        u._key === key ? { ...u, documento: file, errorDoc: "" } : u
      )
    );
  };

  /* Quitar deja url_documento en null, que es lo que el backend lee como
     "borralo". El archivo nuevo, si habia, se descarta. */
  const quitarDoc = (key) =>
    onChange(
      unidades.map((u) =>
        u._key === key ? { ...u, documento: null, url_documento: null, errorDoc: "" } : u
      )
    );

  const cambiar = (key, campo, valor) =>
    onChange(
      unidades.map((u) => {
        if (u._key !== key) return u;
        const siguiente = { ...u, [campo]: valor };
        // Igual que en el producto: sin certificado no hay fecha que guardar.
        if (campo === "certificado_producto_id" && !valor) {
          siguiente.fecha_certificado = "";
        }
        return siguiente;
      })
    );

  return (
    <div className="un-seccion">
      <div className="un-cabecera">
        <span className="un-titulo">
          Units
          {unidades.length > 0 && (
            <span className="un-contador">{unidades.length}</span>
          )}
        </span>

        <button
          type="button"
          className="un-agregar"
          onClick={agregar}
          disabled={disabled}
        >
          <span className="un-agregar-icono">{IconoMas}</span>
          Add unit
        </button>
      </div>

      {unidades.length === 0 ? (
        <p className="un-vacio">
          No units yet. Add one for each physical piece you have in stock —
          they share the part number and differ by serial number.
        </p>
      ) : (
        <div className="un-tabla">
          {/* Las etiquetas una sola vez. Repetirlas en cada linea gastaria
              cinco veces el alto para decir lo mismo. */}
          <div className="un-encabezado" aria-hidden="true">
            <span>Serial number</span>
            <span>Condition</span>
            <span>Certificate</span>
            <span>Certificate date</span>
            <span>PDF</span>
            <span />
          </div>

          {unidades.map((u, i) => {
            const serie = String(u.numero_serie ?? "").trim();
            const duplicada = serie && repetidos.has(serie.toUpperCase());
            const vendida = u.estado === "vendida";
            const bloqueada = disabled || vendida;

            return (
              <div
                key={u._key}
                className={`un-linea ${vendida ? "un-linea--vendida" : ""}`}
              >
                <div className="un-celda">
                  <span className="un-mini-label">Serial number</span>
                  <input
                    className={`un-input ${duplicada ? "un-input--error" : ""}`}
                    value={u.numero_serie}
                    onChange={(e) =>
                      cambiar(u._key, "numero_serie", e.target.value)
                    }
                    disabled={bloqueada}
                    maxLength={80}
                    autoFocus={ultimaAgregada.current === u._key}
                    placeholder="e.g. 4140-0093"
                    aria-label={`Serial number, unit ${i + 1}`}
                  />
                </div>

                <div className="un-celda">
                  <span className="un-mini-label">Condition</span>
                  <SearchSelect
                    name={`condicion-${u._key}`}
                    value={u.condicion_producto_id}
                    onChange={(e) =>
                      cambiar(u._key, "condicion_producto_id", e.target.value)
                    }
                    disabled={bloqueada}
                    options={opcionesCatalogo(condiciones, u.condicion_producto_id)}
                    placeholder="Condition..."
                    searchPlaceholder="Search condition..."
                    maxListHeight={200}
                  />
                </div>

                <div className="un-celda">
                  <span className="un-mini-label">Certificate</span>
                  <SearchSelect
                    name={`certificado-${u._key}`}
                    value={u.certificado_producto_id}
                    onChange={(e) =>
                      cambiar(u._key, "certificado_producto_id", e.target.value)
                    }
                    disabled={bloqueada}
                    options={opcionesCatalogo(
                      certificados,
                      u.certificado_producto_id
                    )}
                    placeholder="Certificate..."
                    searchPlaceholder="Search certificate..."
                    maxListHeight={200}
                  />
                </div>

                <div className="un-celda">
                  <span className="un-mini-label">Certificate date</span>
                  {/* Siempre a la vista aunque todavia no se pueda llenar:
                      escondiendola, quien la buscaba concluia que no existia. */}
                  <input
                    className="un-input"
                    type="date"
                    value={u.fecha_certificado}
                    onChange={(e) =>
                      cambiar(u._key, "fecha_certificado", e.target.value)
                    }
                    disabled={bloqueada || !u.certificado_producto_id}
                    max={hoyISO()}
                    aria-label={`Certificate date, unit ${i + 1}`}
                  />
                </div>

                <div className="un-celda">
                  <span className="un-mini-label">Certificate PDF</span>
                  {u.documento || u.url_documento ? (
                    <div className="un-pdf un-pdf--lleno">
                      <span className="un-pdf-nombre" title={nombreDoc(u)}>
                        {nombreDoc(u)}
                      </span>
                      <button
                        type="button"
                        className="un-pdf-quitar"
                        onClick={() => quitarDoc(u._key)}
                        disabled={bloqueada}
                        aria-label={`Remove PDF from unit ${i + 1}`}
                      >
                        {IconoEquis}
                      </button>
                    </div>
                  ) : (
                    <label className={`un-pdf un-pdf--vacio ${bloqueada ? "un-pdf--off" : ""}`}>
                      <span className="un-pdf-icono">{IconoClip}</span>
                      Attach
                      <input
                        type="file"
                        accept={acceptDe("documento")}
                        onChange={(e) => tomarDoc(u._key, e)}
                        disabled={bloqueada}
                        hidden
                      />
                    </label>
                  )}
                </div>

                <div className="un-celda un-celda-accion">
                  {vendida ? (
                    <span className="un-vendida" title="Already sold in an order">
                      Sold
                    </span>
                  ) : (
                    <button
                      type="button"
                      className="un-quitar"
                      onClick={() => quitar(u._key)}
                      disabled={disabled}
                      title="Remove unit"
                      aria-label={`Remove unit ${serie || i + 1}`}
                    >
                      {IconoBasura}
                    </button>
                  )}
                </div>

                {duplicada && (
                  <p className="un-error" role="alert">
                    Serial number {serie} is already used by another unit.
                  </p>
                )}

                {u.errorDoc && (
                  <p className="un-error" role="alert">
                    {u.errorDoc}
                  </p>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

export default UnidadesProducto;
